// src/features/reporting/filters/DateRangeFilter.tsx
import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react';
import { DateRangePicker, DateRange } from '@mui/x-date-pickers-pro';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Menu,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import {
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInCalendarMonths,
  format,
  subMinutes,
  subHours,
  subDays,
  subMonths,
} from 'date-fns';
import { reportingLogger } from 'helpers/reporting-logger';
import { IFilterFieldProps, FilterCondition } from '../reporting-types';
import { getDateFromProperty } from '../reporting-helpers';
import { useAppState } from '../../../helpers/hooks';

type NullableDateRange = DateRange<Date>;

interface QuickRangeOption {
  id: string;
  label: string;
  getRange: () => NullableDateRange;
  matches: (range: NullableDateRange) => boolean;
  description?: string;
  isCustom?: boolean;
}

const hasCompleteRange = (range: NullableDateRange): range is [Date, Date] => Boolean(range[0] && range[1]);

export const DATE_RANGE_START_FILTER_ID = 'ReportGrid-9001';
export const DATE_RANGE_END_FILTER_ID = 'ReportGrid-9002';

const DateRangeFilter: React.FC<IFilterFieldProps> = ({ config, filters, onChange, isWorking, onInitialized }) => {
  const { actions, areFiltersInitialized } = useAppState();
  const rangeStart = config.options?.rangeStart;
  const rangeEnd = config.options?.rangeEnd;

  const startDefault = useMemo(
    () => getDateFromProperty(rangeStart?.defaultValue) || new Date(),
    [rangeStart?.defaultValue]
  );
  const endDefault = useMemo(
    () => getDateFromProperty(rangeEnd?.defaultValue) || new Date(),
    [rangeEnd?.defaultValue]
  );
  const minDate = useMemo(() => getDateFromProperty(rangeStart?.minValue), [rangeStart?.minValue]);
  const maxDate = useMemo(() => getDateFromProperty(rangeEnd?.maxValue), [rangeEnd?.maxValue]);

  // Use ReportGrid- prefix so filters appear in grid's filter panel
  // Use high numbers (9000+) to avoid conflicts with grid's auto-generated IDs
  const startFilterId = DATE_RANGE_START_FILTER_ID;
  const endFilterId = DATE_RANGE_END_FILTER_ID;

  const startDateFilter = filters?.find((f) => f.id === startFilterId);
  const endDateFilter = filters?.find((f) => f.id === endFilterId);

  // Use state to track whether the end date has been explicitly changed by the user
  const [isEndDateExplicitlySet, setIsEndDateExplicitlySet] = useState(false);

  // Track previous grid filter state to detect user clearing action from grid panel
  const prevHadGridFilter = useRef<boolean | null>(null);

  const currentRange: NullableDateRange = useMemo(
    () => [
      startDateFilter?.value ? new Date(startDateFilter.value as string) : startDefault,
      endDateFilter?.value ? new Date(endDateFilter.value as string) : endDefault,
    ],
    [startDateFilter?.value, endDateFilter?.value, startDefault, endDefault]
  );

  const quickRangeOptions = useMemo<QuickRangeOption[]>(() => {
    const createMinutesOption = (id: string, minutes: number, label: string): QuickRangeOption => ({
      id,
      label,
      getRange: () => [subMinutes(new Date(), minutes), new Date()],
      matches: (range) =>
        hasCompleteRange(range) && Math.abs(differenceInMinutes(range[1], range[0]) - minutes) <= 1,
    });

    const createHoursOption = (id: string, hours: number, label: string): QuickRangeOption => ({
      id,
      label,
      getRange: () => [subHours(new Date(), hours), new Date()],
      matches: (range) =>
        hasCompleteRange(range) && Math.abs(differenceInHours(range[1], range[0]) - hours) <= 0.25,
    });

    const createDaysOption = (id: string, days: number, label: string): QuickRangeOption => ({
      id,
      label,
      getRange: () => [subDays(new Date(), days), new Date()],
      matches: (range) =>
        hasCompleteRange(range) && Math.abs(differenceInDays(range[1], range[0]) - days) <= 0.25,
    });

    const createMonthsOption = (id: string, months: number, label: string): QuickRangeOption => ({
      id,
      label,
      getRange: () => [subMonths(new Date(), months), new Date()],
      matches: (range) => hasCompleteRange(range) && differenceInCalendarMonths(range[1], range[0]) === months,
    });

    return [
      createMinutesOption('last30Minutes', 30, 'Last 30 minutes'),
      createHoursOption('last1Hour', 1, 'Last hour'),
      createHoursOption('last4Hours', 4, 'Last 4 hours'),
      createHoursOption('last12Hours', 12, 'Last 12 hours'),
      createHoursOption('last24Hours', 24, 'Last 24 hours'),
      createDaysOption('last3Days', 3, 'Last 3 days'),
      createDaysOption('last7Days', 7, 'Last 7 days'),
      createDaysOption('last30Days', 30, 'Last 30 days'),
      createDaysOption('last90Days', 90, 'Last 90 days'),
      createMonthsOption('lastMonth', 1, 'Last month'),
      createMonthsOption('last6Months', 6, 'Last 6 months'),
      createMonthsOption('last12Months', 12, 'Last 12 months'),
      {
        id: 'custom',
        label: 'Custom range',
        getRange: () => [null, null],
        matches: () => false,
        isCustom: true,
      },
    ];
  }, []);

  const determineQuickOptionId = useCallback(
    (range: NullableDateRange) => {
      const match = quickRangeOptions.find((option) => !option.isCustom && option.matches(range));
      return match?.id ?? 'custom';
    },
    [quickRangeOptions]
  );

  const [activeOptionId, setActiveOptionId] = useState<string>(() => determineQuickOptionId(currentRange));
  const initialOptionIdRef = useRef<string>(activeOptionId);

  useEffect(() => {
    setActiveOptionId(determineQuickOptionId(currentRange));
  }, [currentRange, determineQuickOptionId]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const menuOpen = Boolean(anchorEl);
  const [pendingOptionId, setPendingOptionId] = useState<string>(activeOptionId);
  const [customRange, setCustomRange] = useState<NullableDateRange>(currentRange);

  useEffect(() => {
    if (menuOpen) return;
    setPendingOptionId(activeOptionId);
    setCustomRange(currentRange);
  }, [menuOpen, activeOptionId, currentRange]);

  const handleRangeChange = useCallback(
    ([start, end]: NullableDateRange, options?: { setEndExplicit?: boolean }) => {
      reportingLogger.verbose('DateRangeFilter: handleRangeChange', { start, end, options }, 'Filter');
      const newFilters: FilterCondition[] = [];

      if (start) {
        const startFormatted = start.toISOString().slice(0, 16);
        newFilters.push({
          id: startFilterId,
          columnField: config.key,
          operatorValue: 'onOrAfter',
          value: startFormatted,
        });
      }

      const shouldSetEnd = options?.setEndExplicit ?? true;

      if (end) {
        const endFormatted = end.toISOString().slice(0, 16);
        newFilters.push({
          id: endFilterId,
          columnField: config.key,
          operatorValue: 'onOrBefore',
          value: endFormatted,
        });
        setIsEndDateExplicitlySet(Boolean(shouldSetEnd));
      } else {
        setIsEndDateExplicitlySet(false);
      }

      if (newFilters.length > 0) {
        onChange(newFilters);
      }
    },
    [config.key, endFilterId, onChange, startFilterId]
  );

  const applyRangeSelection = useCallback(
    (range: NullableDateRange, optionId: string, options?: { closeMenu?: boolean; setEndExplicit?: boolean }) => {
      if (!range[0]) {
        return;
      }

      handleRangeChange(range, { setEndExplicit: options?.setEndExplicit });
      setActiveOptionId(optionId);
      setCustomRange(range);

      if (options?.closeMenu ?? true) {
        setAnchorEl(null);
      }
    },
    [handleRangeChange]
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setPendingOptionId(activeOptionId);
    setCustomRange(currentRange);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleQuickOptionChange = (_: React.ChangeEvent<HTMLInputElement>, optionId: string) => {
    const option = quickRangeOptions.find((item) => item.id === optionId);
    if (!option) return;

    setPendingOptionId(optionId);

    if (option.isCustom) {
      return;
    }

    const newRange = option.getRange();
    applyRangeSelection(newRange, optionId, { closeMenu: true, setEndExplicit: true });
  };

  const handleCustomApply = () => {
    if (!customRange[0] || !customRange[1]) {
      return;
    }
    applyRangeSelection(customRange, 'custom', { closeMenu: true, setEndExplicit: true });
  };

  const handleCustomCancel = () => {
    setAnchorEl(null);
    setCustomRange(currentRange);
    setPendingOptionId(activeOptionId);
  };

  const displayLabel = useMemo(() => {
    if (!currentRange[0] || !currentRange[1]) {
      return 'Select range';
    }

    const active = quickRangeOptions.find((option) => option.id === activeOptionId);
    const isAutomatic = activeOptionId === initialOptionIdRef.current;

    if (active && !active.isCustom) {
      return `${active.label}${isAutomatic ? ' (Automatic)' : ''}`;
    }

    const formatted = `${format(currentRange[0], 'MMM d, yyyy HH:mm')} - ${format(
      currentRange[1],
      'MMM d, yyyy HH:mm'
    )}`;
    return `${active ? active.label : 'Custom range'}: ${formatted}${isAutomatic ? ' (Automatic)' : ''}`;
  }, [activeOptionId, currentRange, quickRangeOptions]);

  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (hasInitializedRef.current || areFiltersInitialized) {
      return;
    }

    const currentStartFilter = filters?.find((f) => f.id === startFilterId);
    const currentEndFilter = filters?.find((f) => f.id === endFilterId);

    if (!currentStartFilter && !currentEndFilter && !isEndDateExplicitlySet) {
      reportingLogger.verbose(
        'DateRangeFilter: Initializing with defaults',
        { startDefault, endDefault },
        'Filter'
      );
      handleRangeChange([startDefault, endDefault], { setEndExplicit: false });
      setActiveOptionId(determineQuickOptionId([startDefault, endDefault]));
    }

    hasInitializedRef.current = true;
    onInitialized?.();
  }, [areFiltersInitialized, determineQuickOptionId, endDefault, filters, handleRangeChange, isEndDateExplicitlySet, onInitialized, startDefault]);

  // Watch for grid filter panel clearing the same column field
  // When user clicks 'X' on any QueuedOnUTC filter in grid panel, also clear DateRangeFilter
  useEffect(() => {
    const hasAnyFilterForThisColumn = filters?.some(
      (f) => f.columnField === config.key && f.id?.startsWith('ReportGrid-')
    );

    if (prevHadGridFilter.current === true && !hasAnyFilterForThisColumn && (startDateFilter || endDateFilter)) {
      reportingLogger.verbose(
        'DateRangeFilter: All grid filters cleared for this column, clearing DateRangeFilter',
        { columnField: config.key },
        'Filter'
      );
      const filtersToRemove = [startDateFilter, endDateFilter].filter((f): f is FilterCondition => Boolean(f));
      if (filtersToRemove.length > 0) {
        actions.removeFiltersAction(filtersToRemove);
      }
      setIsEndDateExplicitlySet(false);
      setActiveOptionId(determineQuickOptionId([startDefault, endDefault]));
    }

    prevHadGridFilter.current = hasAnyFilterForThisColumn ?? false;
  }, [actions, config.key, determineQuickOptionId, endDateFilter, filters, startDateFilter, startDefault, endDefault]);

  const quickOptionsForMenu = quickRangeOptions.filter((option) => !option.isCustom);

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="caption" color="textSecondary">
        Time Range (Local):
      </Typography>
      <Button
        size="small"
        variant="outlined"
        endIcon={<ArrowDropDownIcon />}
        onClick={handleMenuOpen}
        disabled={isWorking}
        sx={{ textTransform: 'none' }}
      >
        {displayLabel}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 320, p: 2 } } }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Time range
            </Typography>
            <RadioGroup value={pendingOptionId} onChange={handleQuickOptionChange}>
              {quickOptionsForMenu.map((option) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  control={<Radio size="small" />}
                  label={option.label}
                  disabled={isWorking}
                />
              ))}
              <Divider sx={{ my: 1 }} />
              <FormControlLabel
                value="custom"
                control={<Radio size="small" />}
                label="Custom range"
                disabled={isWorking}
              />
            </RadioGroup>
          </Box>

          {pendingOptionId === 'custom' && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">Custom range</Typography>
              <DateRangePicker
                value={customRange}
                onChange={(nextRange) => setCustomRange(nextRange)}
                disabled={isWorking}
                minDate={minDate}
                maxDate={maxDate}
                localeText={{ start: rangeStart?.label || 'Start Date', end: rangeEnd?.label || 'End Date' }}
                disableFuture
                slotProps={{
                  textField: {
                    size: 'small',
                    variant: 'outlined',
                  },
                }}
              />
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button size="small" onClick={handleCustomCancel} disabled={isWorking}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleCustomApply}
                  disabled={!customRange[0] || !customRange[1] || isWorking}
                >
                  Apply
                </Button>
              </Box>
            </Stack>
          )}
        </Stack>
      </Menu>
    </Box>
  );
};

export default DateRangeFilter;
